import { useCallback, useEffect, useRef, useState } from 'react';
import { IProduto } from '../../produtos/interfaces/IProduto';

// Variável global para compartilhar o estado do modo teclado
let modoTecladoGlobal = false;

export const isModoTecladoAtivo = () => {
  return modoTecladoGlobal;
};

interface UseModoTecladoProps {
  produtos: IProduto[];
  onProdutoSelecionado: (produto: IProduto) => void;
  onTeclaDigitada?: (tecla: string) => void;
  onF8Pressed?: () => void;
}

export const useModoTeclado = ({ produtos, onProdutoSelecionado, onTeclaDigitada, onF8Pressed }: UseModoTecladoProps) => {
  const [modoTeclado, setModoTeclado] = useState(false);
  const [indiceSelecionado, setIndiceSelecionado] = useState(0);
  const [filtroFocado, setFiltroFocado] = useState(false);
  const inputFiltroRef = useRef<HTMLDivElement>(null);
  const cardsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const areaProdutosRef = useRef<HTMLDivElement>(null);

  // Sincronizar com a variável global
  useEffect(() => {
    modoTecladoGlobal = modoTeclado;
  }, [modoTeclado]);

  // Scroll automático para manter produto selecionado visível
  useEffect(() => {
    if (modoTeclado && produtos.length > 0 && indiceSelecionado >= 0) {
      const cardElement = cardsRefs.current[indiceSelecionado];
      const areaProdutos = areaProdutosRef.current;
      
      if (cardElement && areaProdutos) {
        // Aguardar um pouco para garantir que o DOM foi atualizado
        setTimeout(() => {
          // Usar scrollIntoView com opções mais específicas
          cardElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }, 100);
      }
    }
  }, [indiceSelecionado, modoTeclado, produtos.length]);

  // Detectar mudanças de foco
  useEffect(() => {
    const handleFocusChange = () => {
      const elementoFocado = document.activeElement as HTMLElement;
      
      // Ignorar elementos de dropdown/menu
      if (elementoFocado && (
        elementoFocado.closest('[role="menu"]') ||
        elementoFocado.closest('[role="menuitem"]') ||
        elementoFocado.closest('.MuiMenu-root') ||
        elementoFocado.closest('.MuiMenuItem-root') ||
        elementoFocado.closest('[aria-haspopup="menu"]') ||
        elementoFocado.closest('[aria-controls*="menu"]')
      )) {
        return; // Não alterar o estado do filtro quando focado em elementos de menu
      }
      
      const ehFiltroFocado = elementoFocado && (
        elementoFocado.closest('[placeholder*="Pesquisa"]') || 
        elementoFocado.closest('[placeholder*="pesquisa"]') ||
        elementoFocado.closest('[placeholder*="Buscar"]') ||
        elementoFocado.closest('[placeholder*="buscar"]')
      );
      
      setFiltroFocado(!!ehFiltroFocado);
    };

    // Verificar foco inicial
    handleFocusChange();
    
    // Escutar mudanças de foco
    document.addEventListener('focusin', handleFocusChange);
    document.addEventListener('focusout', handleFocusChange);
    
    return () => {
      document.removeEventListener('focusin', handleFocusChange);
      document.removeEventListener('focusout', handleFocusChange);
    };
  }, []);

  const focarFiltro = useCallback(() => {
    // Tentar múltiplas formas de encontrar o input
    const seletores = [
      'input[placeholder*="Pesquisa"]',
      'input[placeholder*="pesquisa"]',
      'input[placeholder*="Buscar"]',
      'input[placeholder*="buscar"]',
      '.MuiInputBase-input',
      'input[type="text"]'
    ];
    
    let inputElement: HTMLInputElement | null = null;
    
    for (const seletor of seletores) {
      inputElement = document.querySelector(seletor) as HTMLInputElement;
      if (inputElement) {
        break;
      }
    }
    
    if (inputElement) {
      inputElement.focus();
      setFiltroFocado(true);
    } else if (inputFiltroRef.current) {
      // Fallback para o ref
      const textFieldElement = inputFiltroRef.current;
      const actualInput = textFieldElement.querySelector('input') || textFieldElement.querySelector('textarea');
      
      if (actualInput) {
        actualInput.focus();
        setFiltroFocado(true);
      }
    }
  }, []);

  const sairModoTeclado = useCallback(() => {
    setModoTeclado(false);
    setIndiceSelecionado(0);
    setFiltroFocado(false);
    modoTecladoGlobal = false;
  }, []);

  const selecionarProdutoAtual = useCallback(() => {
    if (produtos[indiceSelecionado]) {
      // Simular clique no card do produto para manter a lógica original
      const cardElement = cardsRefs.current[indiceSelecionado];
      if (cardElement) {
        // Encontrar o card dentro do Grid e clicar nele
        const card = cardElement.querySelector('[role="button"], .MuiCard-root, .MuiCardActionArea-root');
        if (card) {
          (card as HTMLElement).click();
        } else {
          // Fallback: clicar no próprio Grid
          cardElement.click();
        }
      } else {
        // Fallback: usar a função original
        onProdutoSelecionado(produtos[indiceSelecionado]);
      }
    }
  }, [produtos, indiceSelecionado, onProdutoSelecionado]);

  // Função para reativar modo teclado após adicionar produto
  const reativarModoTeclado = useCallback(() => {
    if (modoTecladoGlobal) {
      setModoTeclado(true);
      setIndiceSelecionado(0);
      setTimeout(() => {
        focarFiltro();
      }, 50);
    }
  }, [focarFiltro]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const tecla = event.key;
      const ehSeta = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(tecla);
      
      const elementoFocado = document.activeElement as HTMLElement;
      
      // Ignorar elementos de dropdown/menu
      if (elementoFocado && (
        elementoFocado.closest('[role="menu"]') ||
        elementoFocado.closest('[role="menuitem"]') ||
        elementoFocado.closest('.MuiMenu-root') ||
        elementoFocado.closest('.MuiMenuItem-root') ||
        elementoFocado.closest('[aria-haspopup="menu"]') ||
        elementoFocado.closest('[aria-controls*="menu"]')
      )) {
        return; // Não interferir com elementos de menu
      }
      
      // Se estiver focado em um elemento editável que NÃO é o filtro de pesquisa, não fazer nada
      if (elementoFocado && (
        elementoFocado.tagName === 'INPUT' ||
        elementoFocado.tagName === 'TEXTAREA' ||
        elementoFocado.contentEditable === 'true' ||
        elementoFocado.getAttribute('role') === 'textbox' ||
        elementoFocado.classList.contains('MuiInputBase-input') ||
        elementoFocado.classList.contains('MuiOutlinedInput-input') ||
        elementoFocado.classList.contains('MuiFilledInput-input') ||
        elementoFocado.classList.contains('MuiInput-input')
      )) {
        // Se não for o filtro de pesquisa, não fazer nada
        if (!elementoFocado.closest('[placeholder*="Pesquisa"]') && 
            !elementoFocado.closest('[placeholder*="pesquisa"]') &&
            !elementoFocado.closest('[placeholder*="Buscar"]') &&
            !elementoFocado.closest('[placeholder*="buscar"]')) {
          return;
        }
      }
      
      if (ehSeta && !modoTeclado) {
        event.preventDefault();
        setModoTeclado(true);
        setIndiceSelecionado(0);
        focarFiltro();
        return;
      }
    
      if (modoTeclado) {
        const teclasFuncionais = [
          'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
          'Tab', 'CapsLock', 'NumLock', 'ScrollLock',
          'Insert', 'Delete', 'Home', 'End', 'PageUp', 'PageDown',
          'PrintScreen', 'Pause', 'ContextMenu'
        ];
        
        if (teclasFuncionais.includes(tecla)) {
          if (tecla === 'F8' && onF8Pressed) {
            event.preventDefault();
            onF8Pressed();
            return;
          }
          if (tecla === 'F9') {
            // Permitir F9 passar para o contexto
            return;
          }
          return;
        }
        
        if (ehSeta) {
          event.preventDefault();
          
          const colunas = 5;
          
          if (tecla === 'ArrowUp') {
            setIndiceSelecionado(prev => Math.max(0, prev - colunas));
          } else if (tecla === 'ArrowDown') {
            setIndiceSelecionado(prev => Math.min(produtos.length - 1, prev + colunas));
          } else if (tecla === 'ArrowLeft') {
            setIndiceSelecionado(prev => Math.max(0, prev - 1));
          } else if (tecla === 'ArrowRight') {
            setIndiceSelecionado(prev => Math.min(produtos.length - 1, prev + 1));
          }
          return;
        }
    
        if (tecla === 'Enter') {
          event.preventDefault();
          
          // Verificar se está dentro de um drawer de produto
          const elementoFocado = document.activeElement as HTMLElement;
          const ehDrawerProduto = elementoFocado && (
            elementoFocado.closest('[role="presentation"]') ||
            elementoFocado.closest('.MuiDrawer-root') ||
            elementoFocado.closest('[class*="Drawer"]') ||
            elementoFocado.closest('form')
          );
          
          if (ehDrawerProduto) {
            // Tentar clicar no botão de concluir do drawer mais específico
            const drawerElement = elementoFocado.closest('[role="presentation"]') || elementoFocado.closest('.MuiDrawer-root');
            const botaoConcluir = drawerElement?.querySelector('button[type="submit"]') as HTMLButtonElement;
            
            if (botaoConcluir) {
              botaoConcluir.click();
            } else {
              // Fallback: procurar qualquer botão de submit
              const botaoFallback = document.querySelector('button[type="submit"]') as HTMLButtonElement;
              if (botaoFallback) {
                botaoFallback.click();
              }
            }
          } else {
            selecionarProdutoAtual();
          }
          return;
        }
    
        if (tecla === 'Escape') {
          event.preventDefault();
          sairModoTeclado();
          return;
        }
      }
    
      // Se estiver digitando qualquer tecla de texto, focar no filtro (respeitando contexto)
      if (!modoTeclado && !ehSeta) {
        // Permitir combinações de teclas (Ctrl+A, Ctrl+C, Ctrl+V, etc.)
        if (event.ctrlKey || event.altKey || event.shiftKey || event.metaKey) {
          return;
        }
        
        const ehTeclaTexto = event.key.length === 1 && (
          /[a-zA-Z0-9]/.test(event.key) || 
          event.key === ' ' ||
          event.key === 'Backspace'
        );
        
        if (ehTeclaTexto) {
          event.preventDefault();
          focarFiltro();
          
          if (onTeclaDigitada && event.key !== 'Backspace') {
            onTeclaDigitada(event.key);
          }
          return;
        }
      }
      
      // Se estiver digitando qualquer letra/número, resetar modo teclado e focar no filtro
      if (!ehSeta && event.key.length === 1 && /[a-zA-Z0-9]/.test(event.key)) {
        event.preventDefault();
        
        // Resetar modo teclado se estiver ativo
        if (modoTeclado) {
          setModoTeclado(false);
          setIndiceSelecionado(0);
        }
        
        focarFiltro();
        
        if (onTeclaDigitada) {
          onTeclaDigitada(event.key);
        }
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [modoTeclado, produtos, focarFiltro, sairModoTeclado, selecionarProdutoAtual, onTeclaDigitada, onF8Pressed]);

  return {
    modoTeclado,
    indiceSelecionado,
    filtroFocado,
    setFiltroFocado,
    inputFiltroRef,
    cardsRefs,
    areaProdutosRef,
    focarFiltro,
    sairModoTeclado,
    reativarModoTeclado, // Nova função para reativar após adicionar produto
  };
};
